import React from 'react';
import tinycolor from 'tinycolor2';

import {
  HEADER_BASE_HEIGHT_DESKTOP,
  HEADER_BASE_HEIGHT_MOBILE,
  HEADER_MAX_HEIGHT_DESKTOP,
  HERO_DEFAULT_IMAGE_ASPECT,
  HERO_MIN_HEIGHT_DESKTOP,
  HERO_MIN_HEIGHT_MOBILE,
  LEGACY_LOGO_HEIGHT,
  LOGO_BASE_HEIGHT_DESKTOP,
  LOGO_BASE_HEIGHT_MOBILE,
  LOGO_VERTICAL_PADDING,
  POPOVER_MAX_HEIGHT,
} from '../constants/ui';
import { IScrollMetrics, KeyboardKeyEnum } from '../types/shell';

export const isLightColor = (color: string | undefined) => {
  const backgroundColor = tinycolor(color);

  return backgroundColor.isLight();
};

export const isBrightColor = (color?: string) => {
  const validColor = tinycolor(color);

  return validColor ? validColor.getBrightness() > 190 : false;
};

export const getPrimaryTextColor = (bgColor: string | null) => {
  if (!bgColor) {
    return 'text.primary';
  }

  return isLightColor(bgColor) ? 'text.primary' : 'common.white';
};

export const getSecondaryTextColor = (bgColor: string | null) => {
  if (!bgColor) {
    return 'text.secondary';
  }

  return isLightColor(bgColor) ? 'text.secondary' : 'common.white';
};

interface IHeroMinHeightOptions {
  isAdapt: boolean;
  heroWidth: number;
  imageAspect: number;
  mobile: boolean;
}

export const getHeroMinHeight = ({ isAdapt, heroWidth, imageAspect, mobile }: IHeroMinHeightOptions): number => {
  const autoMinHeight = mobile ? HERO_MIN_HEIGHT_MOBILE : HERO_MIN_HEIGHT_DESKTOP;

  if (!isAdapt || !heroWidth) {
    return autoMinHeight;
  }

  const aspect = imageAspect || HERO_DEFAULT_IMAGE_ASPECT;

  return Math.round(heroWidth / aspect);
};

/*
 * App bar logo height in px. `scale` is the stored multiplier (0.5-2, where 1 = 100%).
 *
 * The base is a fixed 32px desktop / 24px mobile
 *
 * null means a legacy experience with no stored value; those keep 48px
 */
export const getLogoHeight = (mobile: boolean, scale: number | null): number => {
  if (scale === null) {
    return LEGACY_LOGO_HEIGHT;
  }

  return Math.round((mobile ? LOGO_BASE_HEIGHT_MOBILE : LOGO_BASE_HEIGHT_DESKTOP) * scale);
};

/*
 * App bar height in px for a given logo scale.
 *
 * Desktop sits at its 64px base for every scale that fits and grows only enough to keep
 * LOGO_VERTICAL_PADDING above and below the logo, so 200% (a 64px logo) yields a 66px bar
 *
 * Mobile is always its 56px base: the logo tops out at 48px there, so it never needs the room.
 */
export const getHeaderHeight = (mobile: boolean, scale: number | null): number => {
  if (mobile) {
    return HEADER_BASE_HEIGHT_MOBILE;
  }

  const required = getLogoHeight(mobile, scale) + LOGO_VERTICAL_PADDING * 2;

  return Math.min(Math.max(HEADER_BASE_HEIGHT_DESKTOP, required), HEADER_MAX_HEIGHT_DESKTOP);
};

interface IPopoverHeightOptions {
  anchorTop: number;
  anchorBottom: number;
  isFlipped: boolean;
  paddingTop: number;
  paddingBottom: number;
  viewportHeight: number;
}

export const getPopoverAvailableHeight = ({
  anchorTop,
  anchorBottom,
  isFlipped,
  paddingTop,
  paddingBottom,
  viewportHeight,
}: IPopoverHeightOptions): number => {
  const space = isFlipped ? anchorTop - paddingTop : viewportHeight - paddingBottom - anchorBottom;

  // No minimum: the panel never exceeds the space actually there, or it would cover the Confirm bar.
  return Math.round(Math.min(Math.max(space, 0), POPOVER_MAX_HEIGHT));
};

// Flips a theme shadow's Y offsets so an elevation casts upward. Used by the sticky filter
// toolbar, whose inverted elevation 8 is clipped to open downward.
export const invertShadowY = (shadow: string): string => shadow.replace(/(^|,)(\s*)0px (\d)/g, '$1$20px -$3');

// True once a scroller has passed `threshold` of its scrollable height — the point at which an
// infinite list requests its next page. A scroller with nothing to scroll counts as at the end,
// or a viewport tall enough to swallow a whole page would strand the rest of the list.
export const hasReachedScrollThreshold = (
  { scrollTop, clientHeight, scrollHeight }: IScrollMetrics,
  threshold: number,
): boolean => {
  if (scrollHeight <= clientHeight) {
    return true;
  }

  return scrollTop + clientHeight >= scrollHeight * threshold;
};

export const isActivationKey = (event: React.KeyboardEvent): boolean =>
  event.key === KeyboardKeyEnum.Enter || event.key === KeyboardKeyEnum.Space;
